import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field } from "@/pages/Login";
import { IMG } from "@/data/images";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", licence_number: "", email: "", password: "", phone: "", documents: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 12) { setError("Password must be at least 12 characters."); return; }
    setLoading(true);
    try {
      const payload = {
        name: form.name, company: form.company, licence_number: form.licence_number,
        email: form.email, password: form.password, phone: form.phone || undefined,
        documents: form.documents ? form.documents.split("\n").map((s) => s.trim()).filter(Boolean) : [],
      };
      await api.post("/auth/register", payload);
      setSubmitted(true);
      toast.success("Application received");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-24 bg-bb-bg order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-10">
            <img src="/logo-mark.png" alt="" className="h-10 w-auto" />
            <span className="font-serif text-lg tracking-[0.18em]">BOTSWANA BRILLIANCE</span>
          </Link>

          {submitted ? (
            <div data-testid="application-received" className="glass rounded-2xl p-10 text-center">
              <div className="grid place-items-center h-16 w-16 rounded-full border border-bb-gold/40 bg-bb-gold/10 mx-auto">
                <ShieldCheck className="h-7 w-7 text-bb-gold" strokeWidth={1.2} />
              </div>
              <h1 className="font-serif text-3xl mt-6">Application received</h1>
              <p className="mt-4 text-sm text-bb-silver/70 leading-relaxed">
                Botswana Brilliance will review your company information and supporting documentation before activating access.
              </p>
              <Link to="/" className="mt-8 inline-block btn-ghost rounded-full px-6 py-3 text-sm">Return home</Link>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-4xl">Apply to Participate</h1>
              <p className="text-sm text-bb-silver/55 mt-2">
                For licensed manufacturers, dealers, brokers, laboratories and technology providers. Applications are reviewed
                and verified before access is activated.
              </p>

              {error && <div data-testid="register-error" className="mt-6 rounded-lg border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">{error}</div>}

              <form onSubmit={submit} className="mt-8 space-y-4">
                <Field label="Full name" testid="register-name" value={form.name} onChange={set("name")} placeholder="Kagiso Molefe" />
                <Field label="Company" testid="register-company" value={form.company} onChange={set("company")} placeholder="Company (Pty) Ltd" />
                <Field label="Licence number" testid="register-licence" value={form.licence_number} onChange={set("licence_number")} placeholder="e.g. BW-MFG-0123" />
                <Field label="Work email" testid="register-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" />
                <Field label="Phone" testid="register-phone" value={form.phone} onChange={set("phone")} placeholder="+267 ..." />
                <Field label="Password (min. 12 characters)" testid="register-password" type="password" value={form.password} onChange={set("password")} placeholder="Choose a strong password" />
                <div>
                  <label className="label-eyebrow">Supporting company documents</label>
                  <textarea
                    data-testid="register-documents"
                    value={form.documents}
                    onChange={(e) => set("documents")(e.target.value)}
                    rows={3}
                    placeholder="List documents you can provide (one per line): e.g. Certificate of Incorporation, Diamond Dealer Licence, Tax Clearance"
                    className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-sm outline-none focus:border-bb-gold/50 transition-colors duration-300 resize-none"
                  />
                </div>
                <p className="text-xs text-bb-silver/45 leading-relaxed">
                  Email verification, company and licence verification, and multi-factor authentication are completed during onboarding.
                  Access is granted only after administrator approval.
                </p>
                <button data-testid="register-submit-btn" disabled={loading} className="btn-gold rounded-full w-full py-3.5 text-sm mt-2">
                  {loading ? "Submitting…" : "Apply to Participate"}
                </button>
              </form>
              <p className="mt-6 text-sm text-bb-silver/55">
                Already approved? <Link to="/login" className="text-bb-gold hover:underline">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
      <div className="relative hidden lg:block overflow-hidden grain order-1 lg:order-2">
        <img src={IMG.portrait2} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-bb-bg via-bb-bg/60 to-bb-bg/30" />
      </div>
    </div>
  );
}
